import { useEffect, useState, type RefObject } from 'react';

/**
 * Attribute on the Build scrollport (`main`) while a secondary sticky header
 * (element list or focus) has replaced the Build header.
 */
export const BUILD_HEADER_STICKY_REPLACE_ATTR =
  'data-element-list-header-sticky';

/**
 * Watches a zero-height sentinel above a sticky header in the Build scrollport.
 * When the sentinel leaves `main`, marks the scroll root so the Build header hides.
 *
 * @returns Whether the observed header is currently sticky.
 */
export const useBuildHeaderStickyReplace = (
  sentinelRef: RefObject<HTMLElement | null>,
): boolean => {
  const [isStickyActive, setIsStickyActive] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const scrollRoot = sentinel.closest('main');
    if (!scrollRoot) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const isSticky = !entry.isIntersecting;
        setIsStickyActive(isSticky);
        scrollRoot.toggleAttribute(BUILD_HEADER_STICKY_REPLACE_ATTR, isSticky);
      },
      { root: scrollRoot, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
      scrollRoot.removeAttribute(BUILD_HEADER_STICKY_REPLACE_ATTR);
    };
  }, [sentinelRef]);

  return isStickyActive;
};
